import React from 'react';
import { Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogTitle, DialogContent, Button, Alert, CircularProgress, Box, Stack } from '@mui/material';

interface StripePaymentFormProps {
  clientSecret: string;
  organizationName?: string;
  stripePromise: Promise<Stripe | null>;
}

function StripePaymentForm({ clientSecret, organizationName, stripePromise }: StripePaymentFormProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#3b82f6',
            colorBackground: '#ffffff',
            colorText: '#1f2937',
          },
        },
        loader: 'auto',
      }}
    >
      <StripeFormContent organizationName={organizationName} />
    </Elements>
  );
}

function StripeFormContent({ organizationName }: { organizationName?: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setError('');

    const { error: submitError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/billing',
      },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message ?? 'An unexpected error occurred');
      setIsLoading(false);
      return;
    }

    setTimeout(() => window.location.reload(), 2000);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
          }}
        />
        <AddressElement
          options={{
            mode: 'billing',
            defaultValues: {
              name: organizationName || '',
            },
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Button type="submit" variant="contained" fullWidth disabled={!stripe || isLoading} size="large">
          {isLoading ? 'Processing...' : 'Save Payment Method'}
        </Button>
      </Stack>
    </form>
  );
}

interface BillingPaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  clientSecret: string;
  stripePromise: Promise<Stripe | null> | null;
  hasPaymentMethod: boolean;
  organizationName?: string;
}

export function BillingPaymentMethodModal({
  open,
  onClose,
  clientSecret,
  stripePromise,
  hasPaymentMethod,
  organizationName,
}: BillingPaymentMethodModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{hasPaymentMethod ? 'Update' : 'Add'} Payment Method</DialogTitle>
      <DialogContent>
        {clientSecret && stripePromise ? (
          <Box sx={{ mt: 2 }}>
            <StripePaymentForm clientSecret={clientSecret} organizationName={organizationName} stripePromise={stripePromise} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Box sx={{ ml: 2, color: 'text.secondary' }}>Loading...</Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
